import { memo, useCallback, useMemo } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Typography, useTheme } from "@mui/material";
import { IRoadLayoutConfig, ILuminaireLayoutConfig, ILuminaireLayoutConfigCallBack, LuminaireLayoutEnum, CarriageWayEnum } from "types";
import { RadioCard } from "components";
import { setObjectURL, getURL, setFromURLObject } from "utils/urls";
import { getLuminaireLayoutImage } from "utils/images";
import getStyles from "./styles";

interface Props {
  roadLayoutConfig: IRoadLayoutConfig;
  luminaireLayoutConfig: ILuminaireLayoutConfig;
  onLuminaireLayoutConfig: ILuminaireLayoutConfigCallBack;
}

const LuminaireLayout: FC<Props> = ({ roadLayoutConfig, luminaireLayoutConfig, onLuminaireLayoutConfig }) => {
  getURL();
  setFromURLObject(luminaireLayoutConfig);

  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const { carriageWay } = roadLayoutConfig;

  const radioGroupBoxHandler = useCallback(
    (value: string | number): void => {
      const nextConfig = { ...luminaireLayoutConfig };

      if ((Object.values(LuminaireLayoutEnum) as string[]).includes(value as string)) {
        nextConfig.luminaireConfig = value as LuminaireLayoutEnum;
      }

      setObjectURL(nextConfig);
      onLuminaireLayoutConfig(nextConfig);
    },
    [luminaireLayoutConfig, onLuminaireLayoutConfig]
  );

  const singleOptions = useMemo(
    () => [
      {
        label: t("Both"),
        value: LuminaireLayoutEnum.BOTH,
        img: getLuminaireLayoutImage("single", "both", roadLayoutConfig),
      },
      {
        label: t("Left"),
        value: LuminaireLayoutEnum.LEFT,
        img: getLuminaireLayoutImage("single", "left", roadLayoutConfig),
      },
      {
        label: t("Right"),
        value: LuminaireLayoutEnum.RIGHT,
        img: getLuminaireLayoutImage("single", "right", roadLayoutConfig),
      },
      {
        label: t("Staggered"),
        value: LuminaireLayoutEnum.STAGGERED,
        img: getLuminaireLayoutImage("single", "staggered", roadLayoutConfig),
      },
      {
        label: t("Rope"),
        value: LuminaireLayoutEnum.ROPE,
        img: getLuminaireLayoutImage("single", "rope", roadLayoutConfig),
      },
    ],
    [roadLayoutConfig, t]
  );

  const doubleOptions = useMemo(
    () => [
      {
        label: t("Both"),
        value: LuminaireLayoutEnum.BOTH,
        img: getLuminaireLayoutImage("double", "both", roadLayoutConfig),
      },
      {
        label: t("Centered double-row"),
        value: LuminaireLayoutEnum.CENTERED,
        img: getLuminaireLayoutImage("double", "double", roadLayoutConfig),
      },
      {
        label: t("Centered single-row"),
        value: LuminaireLayoutEnum.CENTERED_SINGLE,
        img: getLuminaireLayoutImage("double", "single", roadLayoutConfig),
      },
      {
        label: t("Rope"),
        value: LuminaireLayoutEnum.ROPE,
        img: getLuminaireLayoutImage("double", "rope", roadLayoutConfig),
      },
    ],
    [roadLayoutConfig, t]
  );

  return (
    <Stack sx={styles.container}>
      <Typography variant="h2" sx={styles.title}>
        {t("Luminaire Layout")}
      </Typography>
      {carriageWay === CarriageWayEnum.SINGLE && (
        <RadioCard name="luminaireLayout" value={luminaireLayoutConfig.luminaireConfig} onSelect={radioGroupBoxHandler} options={singleOptions} />
      )}
      {carriageWay === CarriageWayEnum.DOUBLE && (
        <RadioCard name="luminaireLayout" value={luminaireLayoutConfig.luminaireConfig} onSelect={radioGroupBoxHandler} options={doubleOptions} />
      )}
    </Stack>
  );
};

LuminaireLayout.displayName = "LuminaireLayout";

export default memo(LuminaireLayout);
